import React, { useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import {
  DatePicker,
  LayoutSideNavigation,
  LayoutWrapperMain,
  LayoutWrapperBusinessSideNav,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
  Page,
  UserNav,
  // BusinessSummary,
  // BusinessTable,
  Select,
  AutocompleteSelect
} from '../../components';
import { TopbarContainer } from '..';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import css from './BusinessServiceFeePage.css';

// ✅ lokalny YYYY-MM-DD bez UTC shift
const toLocalYYYYMMDD = d =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())
    .toISOString()
    .slice(0, 10);

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth();
const firstDay = toLocalYYYYMMDD(new Date(currentYear, currentMonth, 1)); // ✅ 1. dzień m-ca
const lastDay = toLocalYYYYMMDD(now);

export const BusinessResultsPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    location,
    history,
    onLoadData,
    currentUserProviders
  } = props;

  const {
    start = firstDay,
    end = lastDay,
    prev_start,
    prev_end,
    groupingDate = 'paidAt',
    providerId
  } = parse(location.search);

  const isComparisonOn = Boolean(prev_start && prev_end);

  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [error, setError] = useState(null);

  const selectedProvider = currentUserProviders.find(p => p.id.uuid === providerId);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (selectedProvider) {
          const paramsCurrent = {
            [`${groupingDate}GreaterThan`]: start,
            [`${groupingDate}LessThan`]: end,
            currency: selectedProvider.attributes.currency,
            providerId,                // ✅ spójnie
            code: '*'
          };
          const res = await onLoadData(paramsCurrent);
          if (!cancelled) setData(res?.data ?? null);
        }

        if (selectedProvider && prev_start && prev_end) {
          const paramsPrev = {
            [`${groupingDate}GreaterThan`]: prev_start,
            [`${groupingDate}LessThan`]: prev_end,
            currency: selectedProvider.attributes.currency,
            providerId,                // ✅ porównujemy ten sam provider
            code: '*'
            // jeśli miało być porównanie do całego rynku:
            // businessCategory: '*',  // ⬅ wówczas usuń providerId
          };
          const resPrev = await onLoadData(paramsPrev);
          if (!cancelled) setPrevData(resPrev?.data ?? null);
        } else {
          if (!cancelled) setPrevData(null);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e);
      }
    };

    load();
    return () => { cancelled = true; };
    // ✅ pełne zależności
  }, [start, end, prev_start, prev_end, providerId, selectedProvider, groupingDate, onLoadData]);

  const title = intl.formatMessage({ id: 'BusinessResultsPage.title' });

  const handleDateRangeSubmit = ({ start, end, prevStart, prevEnd }) => {
    const prevDates = prevStart && prevEnd ? { prev_start: prevStart, prev_end: prevEnd } : {};
    history.push(
      createResourceLocatorString(
        'BusinessServiceFeePage',
        routeConfiguration(),
        {},
        { start, end, providerId, groupingDate, ...prevDates }
      )
    );
  };

  const handleProviderChange = (option) => {
    const providerUuid = option?.value || option || '';
    history.push(
      createResourceLocatorString(
        'BusinessServiceFeePage',
        routeConfiguration(),
        {},
        { start, end, providerId: providerUuid, groupingDate, prev_start, prev_end }
      )
    );
  };

  const handleGroupingDateChange = (e) => {
    history.push(
      createResourceLocatorString(
        'BusinessServiceFeePage',
        routeConfiguration(),
        {},
        { groupingDate: e.target.value, start, end, providerId, prev_start, prev_end }
      )
    );
  };

  // ✅ helpery
  const tServiceFeeCode = (code) => {
    if (code == null) return intl.formatMessage({ id: 'BusinessServiceFee.total' });
    return intl.formatMessage(
      { id: `BusinessServiceFee.kind.${code}`, defaultMessage: String(code).replace(/-/g, ' ') }
    );
  };

  // --- dopisz obok innych helperów ---
  const formatCurrency = (
    amountCents,
    currency = selectedProvider?.attributes?.currency || 'PLN',
    locale = intl?.locale || 'pl-PL'
  ) => {
    const amount = Number(amountCents ?? 0) / 100;
    if (!Number.isFinite(amount)) return '–';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      useGrouping: true,          // <-- separator tysięcy
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatInt = (value, locale = intl?.locale || 'pl-PL') =>
    new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 })
      .format(Number(value ?? 0));

  // Wiersze dla tabeli (suma + kody)
  const serviceFeeRows = React.useMemo(() => {
    const rows = Array.isArray(data?.data) ? data.data : [];
    return rows.map((item, idx) => {
      const a = item?.attributes || {};
      return {
        key: item?.id || `row-${idx}`,
        // code === null oznacza wiersz sumaryczny
        isTotal: a.code == null,
        code: a.code, // np. "cleaning" | "damage" | "late-return" | null
        paidTransactionsCount: Number(a.paidTransactionsCount || 0),
        pendingTransactionsCount: Number(a.pendingTransactionsCount || 0),
        paidTotal: Number(a.paidTotal || 0),
        pendingTotal: Number(a.pendingTotal || 0),
      };
    });
  }, [data, selectedProvider, intl.locale]);

  const prevByCode = React.useMemo(() => {
    const rows = Array.isArray(prevData?.data) ? prevData.data : [];
    const map = new Map();
    rows.forEach(item => {
      const a = item?.attributes || {};
      const code = a.code ?? null;
      map.set(code, {
        paidTransactionsCount: Number(a.paidTransactionsCount || 0),
        pendingTransactionsCount: Number(a.pendingTransactionsCount || 0),
        paidTotal: Number(a.paidTotal || 0),
        pendingTotal: Number(a.pendingTotal || 0),
      });
    });
    return map;
  }, [prevData]);

  const rowsWithPrev = React.useMemo(() => {
    return serviceFeeRows.map(r => {
      const prev = prevByCode.get(r.isTotal ? null : r.code);
      return {
        ...r,
        hasPrev: Boolean(prev),
        prevPaidTransactionsCount: prev?.paidTransactionsCount ?? null,
        prevPendingTransactionsCount: prev?.pendingTransactionsCount ?? null,
        prevPaidTotal: prev?.paidTotal ?? null,
        prevPendingTotal: prev?.pendingTotal ?? null,
      };
    });
  }, [serviceFeeRows, prevByCode]);


  // Utrzymaj „Razem” na dole
  const totalRow = React.useMemo(
    () => rowsWithPrev.find(r => r.isTotal),
    [rowsWithPrev]
  );
  const codeRows = React.useMemo(
    () => rowsWithPrev.filter(r => !r.isTotal),
    [rowsWithPrev]
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSideNavigation>
        <LayoutWrapperTopbar>
          <TopbarContainer
            currentPage="BusinessServiceFeePage"
            desktopClassName={css.desktopTopbar}
            mobileClassName={css.mobileTopbar}
          />
          <UserNav selectedPageName="BusinessServiceFeePage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperBusinessSideNav currentTab="BusinessServiceFeePage" />
        <LayoutWrapperMain className={css.colorBackground}>
          <div className={css.content}>
            <div className={css.header}>
              <h1 className={css.title}>
                {intl.formatMessage({ id: 'BusinessServiceFee.title' })}
              </h1>
              <DatePicker
                onSubmit={handleDateRangeSubmit}
                value={{ startDate: start, endDate: end, prevStartDate: prev_start, prevEndDate: prev_end }}
                showComparison={false}
              />
            </div>

            <div className={css.filters}>
              <AutocompleteSelect
                label={intl.formatMessage({ id: 'BusinessResults.providerField' })}
                inputValue={{
                  value: providerId,
                  label: currentUserProviders.find(p => p.id.uuid === providerId)?.attributes.name
                    || intl.formatMessage({ id: 'BusinessResults.providerField' })
                }}
                onChange={handleProviderChange}
                options={currentUserProviders.map(p => ({ value: p.id.uuid, label: p.attributes.name }))}
              />
              <Select
                label={intl.formatMessage({ id: 'BusinessDktPage.filtering' })}
                value={groupingDate}
                onChange={handleGroupingDateChange}
              >
                <option value="createdAt">{intl.formatMessage({ id: 'BusinessDktPage.createdAt' })}</option>
                <option value="paidAt">{intl.formatMessage({ id: 'BusinessServiceFeePage.paidAt' })}</option>
              </Select>
            </div>

            {data && (
              <div className={css.tableWrapper}>
                <table className={css.table}>
                  <colgroup>
                    <col />
                    <col className={css.colPaid} />
                    <col className={css.colPaid} />
                    <col className={css.colPending} />
                    <col className={css.colPending} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th rowSpan={2}>{intl.formatMessage({ id: 'BusinessServiceFee.code' })}</th>
                      <th colSpan={2} className={css.groupHeader}>
                        {intl.formatMessage({ id: 'BusinessServiceFee.paidGroup' })}
                      </th>
                      <th colSpan={2} className={css.groupHeaderPending}>
                        {intl.formatMessage({ id: 'BusinessServiceFee.pendingGroup' })}
                      </th>
                    </tr>
                    <tr>
                      <th className={css.num}>{intl.formatMessage({ id: 'BusinessServiceFee.paidTransactionsCount' })}</th>
                      <th className={css.num}>{intl.formatMessage({ id: 'BusinessServiceFee.paidTotal' })}</th>
                      <th className={css.num}>{intl.formatMessage({ id: 'BusinessServiceFee.pendingTransactionsCount' })}</th>
                      <th className={css.num}>{intl.formatMessage({ id: 'BusinessServiceFee.pendingTotal' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codeRows.map(row => (
                      <tr key={row.key}>
                        <td>{tServiceFeeCode(row.code)}</td>

                        <td className={css.num}>
                          <div>{formatInt(row.paidTransactionsCount)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {row.hasPrev ? formatInt(row.prevPaidTransactionsCount) : '—'}
                            </div>
                          )}
                        </td>

                        <td className={css.num}>
                          <div>{formatCurrency(row.paidTotal)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {row.hasPrev ? formatCurrency(row.prevPaidTotal) : '—'}
                            </div>
                          )}
                        </td>

                        <td className={css.num}>
                          <div>{formatInt(row.pendingTransactionsCount)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {row.hasPrev ? formatInt(row.prevPendingTransactionsCount) : '—'}
                            </div>
                          )}
                        </td>

                        <td className={css.num}>
                          <div>{formatCurrency(row.pendingTotal)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {row.hasPrev ? formatCurrency(row.prevPendingTotal) : '—'}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}

                    {totalRow && (
                      <tr key={totalRow.key} className={css.totalRow}>
                        <td>{intl.formatMessage({ id: 'BusinessServiceFee.total' })}</td>

                        <td className={css.num}>
                          <div>{formatInt(totalRow.paidTransactionsCount)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {totalRow.hasPrev ? formatInt(totalRow.prevPaidTransactionsCount) : '—'}
                            </div>
                          )}
                        </td>

                        <td className={css.num}>
                          <div>{formatCurrency(totalRow.paidTotal)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {totalRow.hasPrev ? formatCurrency(totalRow.prevPaidTotal) : '—'}
                            </div>
                          )}
                        </td>

                        <td className={css.num}>
                          <div>{formatInt(totalRow.pendingTransactionsCount)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {totalRow.hasPrev ? formatInt(totalRow.prevPendingTransactionsCount) : '—'}
                            </div>
                          )}
                        </td>

                        <td className={css.num}>
                          <div>{formatCurrency(totalRow.pendingTotal)}</div>
                          {isComparisonOn && (
                            <div className={css.prevValue}>
                              {totalRow.hasPrev ? formatCurrency(totalRow.prevPendingTotal) : '—'}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}

                    {rowsWithPrev.length === 0 && (
                      <tr>
                        <td colSpan={5} className={css.emptyCell}>
                          {intl.formatMessage({ id: 'BusinessServiceFee.empty' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {error && (
              <div className={css.error}>
                {intl.formatMessage({ id: 'BusinessResults.errorLoading' })}
              </div>
            )}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser, userProviders: currentUserProviders } = state.user;
  return {
    currentUser,
    currentUserProviders,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const loadData = params => (dispatch, getState, sdk) => {
  return sdk.newSdk.analyticsServiceFees.query({ ...params })
    .then(res => res)
    .catch(e => {
      // ✅ przekaż błąd do góry, żeby setError zadziałał
      throw e;
    });
};

const mapDispatchToProps = dispatch => ({
  onLoadData: (params) => dispatch(loadData(params))
});

const BusinessResultsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withRouter
)(BusinessResultsPageComponent);

export default BusinessResultsPage;
